import React, { useMemo, useState } from 'react'
import { formatFleetValue, isUnitBillable } from '../fleetLiveHelpers'
import { getPumpDashboardFields } from './pumpCardHelpers'

const FIELD_DECIMALS = {
  Gear: 0,
  RPM: 0,
  DischargePressure: 0,
  'Discharge Pressure': 0,
  DischargeRate: 1,
  'Discharge Rate': 1,
  Fuel: 1,
  Diesel: 1,
  GasSub: 1,
  'Gas Sub': 1,
  TransTemp: 0,
  'Trans Temp': 0,
  CoolantTemp: 0,
  'Coolant Temp': 0,
  Suction: 1,
  RodLoad: 1,
  'Rod Load': 1,
}

const decimalsFor = (fieldName) => {
  if (FIELD_DECIMALS[fieldName] != null) return FIELD_DECIMALS[fieldName]
  const compact = String(fieldName || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
  const match = Object.entries(FIELD_DECIMALS).find(
    ([key]) => key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() === compact
  )
  return match ? match[1] : 1
}

const FALLBACK_COLUMNS = [
  { key: 'Gear', label: 'Gear', decimals: 0 },
  { key: 'RPM', label: 'RPM', decimals: 0 },
  { key: 'DischargePressure', label: 'DischargePressure', decimals: 0 },
  { key: 'DischargeRate', label: 'DischargeRate', decimals: 1 },
  { key: 'Fuel', label: 'Fuel', decimals: 1 },
  { key: 'GasSub', label: 'GasSub', decimals: 1 },
  { key: 'TransTemp', label: 'TransTemp', decimals: 0 },
  { key: 'CoolantTemp', label: 'CoolantTemp', decimals: 0 },
  { key: 'Suction', label: 'Suction', decimals: 1 },
  { key: 'RodLoad', label: 'RodLoad', decimals: 1 },
]

/** Default: units as columns (top), channels as rows. Alternate: channels as columns. */
export const READINGS_LAYOUT_UNITS_TOP = 'unitsTop'
export const READINGS_LAYOUT_CHANNELS_TOP = 'channelsTop'

const PumpTable = ({
  pumps = [],
  onSelectReading,
  selectedFieldName,
  layout = READINGS_LAYOUT_UNITS_TOP,
  waitingLabel = 'Waiting for data…',
  unbillableLabel = 'Not in subscription',
}) => {
  const channels = useMemo(() => {
    const seen = new Set()
    const cols = []
    pumps.forEach((unit) => {
      getPumpDashboardFields(unit).forEach((field) => {
        if (seen.has(field.fieldName)) return
        seen.add(field.fieldName)
        cols.push({
          key: field.fieldName,
          label: field.fieldName,
          decimals: decimalsFor(field.fieldName),
          unit: field.unit,
        })
      })
    })
    return cols.length ? cols : FALLBACK_COLUMNS
  }, [pumps])

  const fieldsByUnit = useMemo(() => {
    const map = {}
    pumps.forEach((unit) => {
      map[unit.unitUId] = getPumpDashboardFields(unit).reduce((acc, field) => {
        acc[field.fieldName] = field
        return acc
      }, {})
    })
    return map
  }, [pumps])

  const [hoveredChannelKey, setHoveredChannelKey] = useState(null)

  if (!pumps.length) {
    return (
      <div className="fleetEmptyState">
        {waitingLabel}
      </div>
    )
  }

  const getCellValue = (unit, channelKey) => {
    const field = fieldsByUnit[unit.unitUId]?.[channelKey]
    return field ? field.value : unit.pumpData?.[channelKey]
  }

  const handleChannelSelect = (fieldName) => {
    if (fieldName) onSelectReading?.(fieldName)
  }

  if (layout === READINGS_LAYOUT_CHANNELS_TOP) {
    return (
      <div className="fleetTableWrap">
        <table
          className="fleetLiveTable fleetLiveTable--channelsTop"
          onMouseLeave={() => setHoveredChannelKey(null)}
        >
          <thead>
            <tr>
              <th>Unit</th>
              {channels.map((col) => {
                const isHovered = hoveredChannelKey === col.key
                const isActive = selectedFieldName === col.key
                return (
                  <th
                    key={col.key}
                    className={`fleetLiveTableNumHead${isHovered ? ' is-colHover' : ''}${isActive ? ' is-activeChannel' : ''}`}
                    onMouseEnter={() => setHoveredChannelKey(col.key)}
                    onClick={() => handleChannelSelect(col.key)}
                  >
                    {col.label}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {pumps.map((unit) => (
              <tr
                key={unit.unitUId}
                className={!isUnitBillable(unit) ? 'is-unbillable' : ''}
                title={!isUnitBillable(unit) ? unbillableLabel : undefined}
              >
                <td className="fleetLiveTableUnit">
                  {unit.unitName || unit.unitUId}
                </td>
                {channels.map((col) => {
                  const isHovered = hoveredChannelKey === col.key
                  const isActive = selectedFieldName === col.key
                  return (
                    <td
                      key={col.key}
                      className={`fleetLiveTableNum${isHovered ? ' is-colHover' : ''}${isActive ? ' is-colActive' : ''}`}
                      onMouseEnter={() => setHoveredChannelKey(col.key)}
                      onClick={() => handleChannelSelect(col.key)}
                    >
                      {formatFleetValue(getCellValue(unit, col.key), col.decimals)}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  // Default: units across the top, channels down the side
  return (
    <div className="fleetTableWrap">
      <table className="fleetLiveTable fleetLiveTable--unitsTop">
        <thead>
          <tr>
            <th className="fleetLiveTableChannel">Channel</th>
            {pumps.map((unit) => (
              <th
                key={unit.unitUId}
                className={`fleetLiveTableUnit${!isUnitBillable(unit) ? ' is-unbillable' : ''}`}
                title={!isUnitBillable(unit) ? unbillableLabel : undefined}
              >
                {unit.unitName || unit.unitUId}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {channels.map((col) => {
            const isSelectedField = selectedFieldName === col.key
            return (
              <tr
                key={col.key}
                className={isSelectedField ? 'is-selected is-chartActive' : ''}
                onClick={() => handleChannelSelect(col.key)}
              >
                <td className="fleetLiveTableChannel">
                  {col.label}
                </td>
                {pumps.map((unit) => (
                  <td
                    key={unit.unitUId}
                    className={`fleetLiveTableNum${isSelectedField ? ' is-selectedField' : ''}${!isUnitBillable(unit) ? ' is-unbillable' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleChannelSelect(col.key)
                    }}
                  >
                    {formatFleetValue(getCellValue(unit, col.key), col.decimals)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default PumpTable
